import { useState, useEffect } from "react";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Loader2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Phone } from "lucide-react";

interface Department {
  id: number;
  name: string;
  sigla: string | null;
}

interface UserProfile {
  id: string;
  full_name: string | null;
  department_id: number | null;
  position: string | null;
  phone: string | null;
  created_at: string | null;
  updated_at: string | null;
}

const UserSettings = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [user, setUser] = useState<any>(null);

  // Form state
  const [fullName, setFullName] = useState("");
  const [departmentId, setDepartmentId] = useState<string>("");
  const [position, setPosition] = useState("");
  const [phone, setPhone] = useState("");

  useEffect(() => {
    createProfilesTable();
    loadUser();
    loadDepartments();
  }, []);

  // Adiciona useEffect para carregar o perfil quando o usuário estiver disponível
  useEffect(() => {
    if (user?.id) {
      loadProfile();
    }
  }, [user]);

  const createProfilesTable = async () => {
    try {
      // Verifica se a tabela existe
      const { error: checkError } = await supabase
        .from('user_profiles')
        .select('id')
        .limit(1);

      if (checkError && checkError.code === '42P01') {
        // Tabela não existe, vamos criá-la
        const { error: createError } = await supabase
          .rpc('create_user_profiles_table');

        if (createError) {
          console.error('Erro ao criar tabela:', createError);
          return;
        }
      }
    } catch (error) {
      console.error('Erro ao verificar/criar tabela:', error);
    }
  };

  const loadUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      setUser(user);
      // Cria o perfil do usuário se não existir
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error && error.code === 'PGRST116') {
        // Perfil não existe, vamos criar
        const { error: insertError } = await supabase
          .from('user_profiles')
          .insert({
            id: user.id,
            full_name: '',
            department_id: null,
            position: '',
            phone: '',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          });

        if (insertError) {
          console.error('Erro ao criar perfil:', insertError);
        } else {
          // Se criou com sucesso, carrega o perfil
          loadProfile();
        }
      } else if (data) {
        // Se encontrou o perfil, atualiza os estados
        setProfile(data);
        setFullName(data.full_name || '');
        setDepartmentId(data.department_id?.toString() || '');
        setPosition(data.position || '');
        setPhone(data.phone || '');
      }
    }
  };

  const loadDepartments = async () => {
    try {
      const { data, error } = await supabase
        .from("departments")
        .select("id, name, sigla")
        .order("name");

      if (error) throw error;
      setDepartments(data || []);
    } catch (error: any) {
      toast({
        title: "Erro ao carregar secretarias",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const loadProfile = async () => {
    try {
      setLoading(true);

      if (!user?.id) {
        throw new Error("Usuário não autenticado");
      }

      console.log("Carregando perfil para o usuário:", user.id);

      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error("Erro ao carregar perfil:", error);
        if (error.code === 'PGRST116') {
          // Perfil não existe, vamos criar
          const { error: insertError } = await supabase
            .from('user_profiles')
            .insert({
              id: user.id,
              full_name: '',
              department_id: null,
              position: '',
              phone: '',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            });

          if (insertError) {
            console.error('Erro ao criar perfil:', insertError);
            throw insertError;
          }
        } else {
          throw error;
        }
      }

      console.log("Perfil carregado:", data);

      if (data) {
        setProfile(data);
        setFullName(data.full_name || "");
        setDepartmentId(data.department_id?.toString() || "");
        setPosition(data.position || "");
        setPhone(data.phone || "");
      }
    } catch (error: any) {
      console.error("Erro ao carregar perfil:", error);
      toast({
        title: "Erro ao carregar perfil",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);

      if (!user?.id) {
        throw new Error("Usuário não autenticado");
      }

      const profileData = {
        id: user.id,
        full_name: fullName.trim(),
        department_id: departmentId ? parseInt(departmentId) : null,
        position: position.trim(),
        phone: phone ? (phone.startsWith("+55") ? phone : `+55${phone}`) : null,
        updated_at: new Date().toISOString(),
      };

      console.log("Dados a serem salvos:", profileData);

      const { data, error } = await supabase
        .from('user_profiles')
        .upsert(profileData)
        .select()
        .single();

      if (error) {
        console.error("Erro ao salvar:", error);
        throw error;
      }

      console.log("Dados salvos com sucesso:", data);

      toast({
        title: "Sucesso",
        description: "Perfil atualizado com sucesso!",
      });

      await loadProfile();
    } catch (error: any) {
      console.error("Erro completo:", error);
      toast({
        title: "Erro ao atualizar perfil",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Configurações</h1>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList>
          <TabsTrigger value="profile">Perfil</TabsTrigger>
          <TabsTrigger value="account">Conta</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-4">
                <Avatar className="h-24 w-24">
                  <AvatarImage src={user.user_metadata?.avatar_url} />
                  <AvatarFallback>{fullName?.charAt(0) || user.email?.charAt(0)}</AvatarFallback>
                </Avatar>
                <div>
                  <CardTitle>Informações do Perfil</CardTitle>
                  <CardDescription>
                    Atualize suas informações pessoais e profissionais
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="fullName">Nome Completo</Label>
                  <Input
                    id="fullName"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Seu nome completo"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="department">Secretaria</Label>
                  <Select
                    value={departmentId}
                    onValueChange={setDepartmentId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione uma secretaria" />
                    </SelectTrigger>
                    <SelectContent>
                      {departments.map((dept) => (
                        <SelectItem key={dept.id} value={dept.id.toString()}>
                          {dept.sigla ? `${dept.sigla} - ${dept.name}` : dept.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="position">Cargo</Label>
                  <Input
                    id="position"
                    value={position}
                    onChange={(e) => setPosition(e.target.value)}
                    placeholder="Seu cargo"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">
                    <Phone className="h-4 w-4 inline mr-2" />
                    Telefone
                  </Label>
                  <Input
                    id="phone"
                    value={phone}
                    onChange={(e) => {
                      let value = e.target.value;
                      // Remove qualquer +55 existente para evitar duplicação
                      if (value.startsWith("+55")) {
                        value = value.substring(3);
                      }
                      // Remove caracteres não numéricos
                      value = value.replace(/\D/g, "");
                      setPhone(value);
                    }}
                    placeholder="(00) 00000-0000"
                  />
                  <p className="text-sm text-muted-foreground">
                    Será adicionado automaticamente o prefixo +55
                  </p>
                </div>

                <Button type="submit" disabled={loading}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Salvar Alterações
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="account" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Informações da Conta</CardTitle>
              <CardDescription>
                Gerencie suas informações de acesso
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <div className="flex items-center space-x-2">
                  <Input
                    value={user.email}
                    disabled
                    className="bg-muted"
                  />
                  <Button variant="outline" disabled>
                    Verificado
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  Este é o email usado para acessar sua conta
                </p>
              </div>

              <div className="space-y-2">
                <Label>Senha</Label>
                <div className="flex items-center space-x-2">
                  <Input
                    type="password"
                    value="••••••••"
                    disabled
                    className="bg-muted"
                  />
                  <Button variant="outline" onClick={() => toast({
                    title: "Em breve",
                    description: "A funcionalidade de alterar senha estará disponível em breve!",
                  })}>
                    Alterar
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  Altere sua senha periodicamente para manter sua conta segura
                </p>
              </div>

              <div className="space-y-2">
                <Label>Sessão Atual</Label>
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium">Último acesso</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(user.last_sign_in_at).toLocaleString('pt-BR')}
                    </p>
                  </div>
                  <Button 
                    variant="destructive"
                    onClick={async () => {
                      await supabase.auth.signOut();
                      window.location.href = '/login';
                    }}
                  >
                    Sair
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default UserSettings;
